import { AfterViewInit, Component, computed, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { SelectFolderWorkgroupDialogStore } from './select-folder-workgroup-dialog.store';
import { TableColumn, TableColumnType } from '../../../../../shared/components/kit/table/table.types';
import { fb, routeParams } from '../../../../../shared/shared.global-service';
import { ConfirmBuilder } from '../../../../../shared/service/confirm.builder';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { FilterInput } from '@it2go/data-grid';
import { FolderWorkgroupCreateInput, FolderWorkgroupGridItemObject, FolderWorkgroupUpdateInput } from '@it2go/types';
import { dateify } from '../../../../../shared/helpers/date.helper';
import { Validators } from '@angular/forms';
import { contentGlobalSuccess } from '../../../../content.error';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { SaveButtonComponent } from '../../../../../../components/kit/button/save-button.component';
import { CancelButtonComponent } from '../../../../../../components/kit/button/cancel-button.component';
import { CardComponent } from '../../../../../../components/kit/card/card.component';
import { CardTableComponent } from '../../../../../shared/components/kit/table/card-table.component';
import { changeSignal } from '../../../../../../utils/form.utils';

@Component({
    selector: 'app-select-folder-workgroup-dialog',
    templateUrl: './select-folder-workgroup-dialog.component.html',
    styleUrls: ['./select-folder-workgroup-dialog.component.scss'],
    providers: [SelectFolderWorkgroupDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectInputComponent,
        DateInputComponent,
        SaveButtonComponent,
        CancelButtonComponent,
        CardComponent,
        CardTableComponent,
    ],
})
export class SelectFolderWorkgroupDialogComponent extends BaseDialogComponent implements AfterViewInit {

    protected readonly store = inject(SelectFolderWorkgroupDialogStore);
    private readonly confirm = inject(ConfirmBuilder);

    @ViewChild('folderWorkgroupTable') folderWorkgroupTable!: CardTableComponent;

    protected filters: FilterInput[][] = [];
    protected columns: TableColumn[] = [
        {
            name: 'name',
            dataKey: 'serviceWorkgroupName',
        },
        {
            name: 'validFrom',
            type: TableColumnType.Date,
            filterable: undefined,
        },
        {
            name: 'validTo',
            type: TableColumnType.Date,
            filterable: undefined,
        },
    ];

    protected form = fb.group({
        id: fb.control<number | null>(null),
        serviceWorkgroupId: fb.control<number | null>(null, Validators.required),
        validFrom: fb.control<Date | null>(null, Validators.required),
    });

    protected headerTitle = computed(() => {
        const groupId = this.formServiceWorkgroupChanged();
        if (groupId) {
            return 'folder.workgroups.editGroup';
        } else {
            return 'folder.workgroups.newGroup';
        }
    });

    private formServiceWorkgroupChanged = changeSignal<number | null>(this.form.controls.serviceWorkgroupId);

    ngAfterViewInit() {
        this.store.refreshGrid(this.folderWorkgroupTable, 'createFolderWorkgroup', 'updateFolderWorkgroup', 'deleteFolderWorkgroup');

        const folderId = routeParams.folderId();
        const serviceId = this.store.serviceId();
        if (serviceId && folderId) {
            this.store.api.getServiceWorkgroups(FilterBuilder.singlePage({
                serviceId,
                active: true,
            }));
            this.filters = [
                [{ column: 'folderId', operator: 'EQ', value: [folderId] }],
                [{ column: 'active', operator: 'EQ', value: ['1'] }],
            ];
            this.form.reset();
        }
    }

    protected openUpdate(folderWorkgroup: FolderWorkgroupGridItemObject) {
        this.form.patchValue({
            id: folderWorkgroup.id,
            serviceWorkgroupId: folderWorkgroup.serviceWorkgroupId,
            validFrom: dateify(folderWorkgroup.validFrom),
        });
    }

    protected async submit() {
        if (this.form.isInvalid()) return;

        const _data = {
            validFrom: this.form.value.validFrom?.toISOString() || null,
        };

        if (this.form.value.id) {
            const data = _data as FolderWorkgroupUpdateInput;
            data.id = this.form.value.id;
            await this.store.api.updateFolderWorkgroup(data);
            contentGlobalSuccess.next('common.updated');
        } else {
            const data = _data as FolderWorkgroupCreateInput;
            data.folderId = Number(routeParams.folderId());
            data.serviceWorkgroupId = this.form.value.serviceWorkgroupId!;
            await this.store.api.createFolderWorkgroup(data);
            contentGlobalSuccess.next('common.created');
        }

        this.form.reset();
    }

    protected deleteFolderWorkgroup(event: any) {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteFolderWorkgroup({ id: event.item.id });
                contentGlobalSuccess.next('common.deleted');
            },
            event,
        );
    }
}
